/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import {
  ActionIcon,
  Card,
  Form, FormFieldProps as LibraryFormFieldProps, Grid, HStack, Icon, ICON_CLOSE, ICON_INFORMATION_OUTLINE, ICON_MAGNIFY, Link, Stack, Text, useFormContext,
} from '@doveit/bricks';
import useGoogleMapsAPI from '../../../hooks/use-google-maps-api/useGoogleMapsAPI';
import { AddressSuggestion } from '../../../providers/geo/dtos';
import { geocodeByAddress } from '../../../providers/geo/geocode/geocodeProvider';
import { isKeyOf } from '../../../utils/object/object';
import { INPUT_PLACEHOLDERS } from '../../../constants';
import { GeoFormModel } from './types';
import { getNormalizedAddress } from '../../../utils/geo/utils';

export interface FormGeoFieldProps extends React.AriaAttributes, Pick<LibraryFormFieldProps, 'help' | 'oldValue'> {
  name: string,
  labels?: Partial<Record<(typeof SHOWN_FIELDS)[number], string>>,
  required?: boolean | Partial<Record<(typeof SHOWN_FIELDS)[number], boolean>>,
  disabled?: boolean | Partial<Record<(typeof SHOWN_FIELDS)[number], boolean>>,
  enableManualEntry?: boolean,
  toggleManualEntry?: boolean,

}

const SHOWN_FIELDS = [
  'normalizedAddress',
  'route',
  'streetNumber',
  'plateCode',
  'postalCode',
  'locality',
] as const;

const GEO_FIELDS = [
  'route',
  'streetNumber',
  'plateCode',
  'postalCode',
  'locality',
  'administrativeAreaLevelOne',
  'administrativeAreaLevelTwo',
  'postalCode',
  'latitude',
  'longitude',
  'suburb',
];

/**
 * @todo move this in Bricks when we also move google maps stuff there
 */
const FormGeoField: React.FC<FormGeoFieldProps> = ({
  name,
  labels,
  required = false,
  disabled = false,
  enableManualEntry = false,
  toggleManualEntry = false,

  help,
  oldValue,
  ...rest
}) => {
  const { getAddressSuggestions } = useGoogleMapsAPI();
  const { setFieldValue, values } = useFormContext<{ [name]: GeoFormModel }>();
  const [isManualEntryToggled, setIsManualEntryToggled] = React.useState(toggleManualEntry);

  const handleAddressSuggestionSelected = React.useCallback(async (suggestion: AddressSuggestion | null) => {
    GEO_FIELDS.forEach((field) => {
      setFieldValue(`${name}.${field}`, '');
    });

    if (!suggestion) return;

    const { description } = suggestion;
    const [geoCode] = await geocodeByAddress(description);

    GEO_FIELDS.forEach((field) => {
      if (isKeyOf(geoCode, field)) {
        setFieldValue(`${name}.${field}`, geoCode[field] || '');
      }
    });
  }, [name, setFieldValue]);

  const handleToggleManualEntry = React.useCallback(() => {
    setFieldValue(`${name}.normalizedAddress`, null);

    GEO_FIELDS.forEach((field) => {
      setFieldValue(`${name}.${field}`, '');
    });

    setIsManualEntryToggled((prev) => !prev);
  }, [name, setFieldValue]);

  const onManualFieldChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(`${name}.normalizedAddress.description`, getNormalizedAddress({ ...values[name], [event.target.name.split('.')[1]]: event.target.value }));
  }, [name, setFieldValue, values]);

  return (
    <Stack gap={150} {...rest}>
      <Form.AutoCompleteV2
        name={`${name}.normalizedAddress`}
        label={labels?.normalizedAddress || 'Indirizzo'}
        aria-label={`Campo per inserire ${labels?.normalizedAddress || 'Indirizzo'}`}
        required={typeof required === 'boolean' ? required : required.normalizedAddress}
        icon={ICON_MAGNIFY}
        suggestions={getAddressSuggestions}
        placeholder={INPUT_PLACEHOLDERS.PROPERTY_ADDRESS}
        printSuggestion={({ description }) => description}
        onSuggestionSelected={handleAddressSuggestionSelected}
        disabled={(typeof disabled === 'boolean' ? disabled : disabled.normalizedAddress) || isManualEntryToggled}
        help={help}
        oldValue={oldValue}
      />

      {enableManualEntry && (
        <HStack>
          <Icon
            path={ICON_INFORMATION_OUTLINE}
            size={16}
          />
          <Text.BodySmall>
            {isManualEntryToggled ? ' Vuoi cercare un nuovo indirizzo?' : 'Non hai trovato l\'indirizzo?'}
            {' '}
            <Link
              role="button"
              aria-label={isManualEntryToggled ? 'Cerca un nuovo indirizzo' : 'Inserisci l\'indirizzo manualmente'}
              onClick={handleToggleManualEntry}
            >
              <strong>Clicca qui</strong>
            </Link>
          </Text.BodySmall>
        </HStack>
      )}

      {isManualEntryToggled && (
        <Card>
          <Card.Header
            title="Indirizzo"
            primaryActions={[
              <ActionIcon
                size="S"
                label="Cerca nuovo indirizzo"
                icon={{
                  path: ICON_CLOSE,
                }}
                onClick={handleToggleManualEntry}
              />,
            ]}
          />
          <Card.Content>
            <Grid gutter={100}>
              <Grid.Unit size={{ MD: 3 / 4 }}>
                <Form.Input
                  name={`${name}.route`}
                  label={labels?.route || 'Strada'}
                  aria-label={`Campo per inserire ${labels?.route || 'Strada'}`}
                  placeholder={INPUT_PLACEHOLDERS.PROPERTY_ROUTE}
                  disabled={typeof disabled === 'boolean' ? disabled : disabled.route}
                  required={typeof required === 'boolean' ? required : required.route}
                  onChange={onManualFieldChange}
                />
              </Grid.Unit>

              <Grid.Unit size={{ MD: 1 / 4 }}>
                <Form.Input
                  name={`${name}.streetNumber`}
                  label={labels?.streetNumber || 'Numero civico'}
                  aria-label={`Campo per inserire ${labels?.streetNumber || 'Numero civico'}`}
                  placeholder={INPUT_PLACEHOLDERS.PROPERTY_STREET_NUMBER}
                  disabled={typeof disabled === 'boolean' ? disabled : disabled.streetNumber}
                  required={typeof required === 'boolean' ? required : required.streetNumber}
                  onChange={onManualFieldChange}
                />
              </Grid.Unit>

              <Grid.Unit size={{ MD: 1 / 2 }}>
                <Form.Input
                  name={`${name}.locality`}
                  label={labels?.locality || 'Comune'}
                  aria-label={`Campo per inserire ${labels?.locality || 'Comune'}`}
                  placeholder={INPUT_PLACEHOLDERS.PROPERTY_LOCALITY}
                  disabled={typeof disabled === 'boolean' ? disabled : disabled.locality}
                  required={typeof required === 'boolean' ? required : required.locality}
                  onChange={onManualFieldChange}
                />
              </Grid.Unit>

              <Grid.Unit size={{ MD: 1 / 4 }}>
                <Form.Input
                  name={`${name}.postalCode`}
                  label={labels?.postalCode || 'CAP'}
                  aria-label={`Campo per inserire ${labels?.postalCode || 'CAP'}`}
                  placeholder={INPUT_PLACEHOLDERS.PROPERTY_POSTAL_CODE}
                  disabled={typeof disabled === 'boolean' ? disabled : disabled.postalCode}
                  required={typeof required === 'boolean' ? required : required.postalCode}
                  onChange={onManualFieldChange}
                />
              </Grid.Unit>

              <Grid.Unit size={{ MD: 1 / 4 }}>
                <Form.Input
                  name={`${name}.plateCode`}
                  label={labels?.plateCode || 'Provincia (sigla)'}
                  aria-label={`Campo per inserire ${labels?.plateCode || 'Provincia'}`}
                  placeholder={INPUT_PLACEHOLDERS.PROPERTY_PLATE_CODE}
                  disabled={typeof disabled === 'boolean' ? disabled : disabled.plateCode}
                  required={typeof required === 'boolean' ? required : required.plateCode}
                  onChange={onManualFieldChange}
                  minLength={2}
                  maxLength={2}
                />
              </Grid.Unit>
            </Grid>
          </Card.Content>
        </Card>
      )}
    </Stack>
  );
};

export default FormGeoField;
